/**
 * Documentos (EL-188) — uma ÁREA do aplicativo, alcançada pela barra lateral.
 *
 * O QUE ESTA TELA É, depois do pedido de 30/07/2026
 * Uma lista de PASTAS, uma por empreendimento da incorporadora, que expandem no lugar
 * para mostrar os arquivos. Não há nível de incorporadora na navegação: quando o
 * aplicativo já sabe de quem são as pastas (porque a pessoa abriu um empreendimento ou um
 * cenário), ele vai direto. A escolha da incorporadora só aparece no caso em que não há
 * como saber — e some assim que houver contexto.
 *
 * ⚠ O NÍVEL DE INCORPORADORA SAIU DA TELA, NÃO DO ARMAZENAMENTO. O arquivo continua em
 * `incorporadora/empreendimento/arquivo`, porque o primeiro segmento é a chave de
 * autorização: é ele que impede uma incorporadora de alcançar o arquivo da outra, com um
 * CHECK no banco e a política do compartimento. Esconder ≠ remover.
 *
 * ⚠ NADA AQUI DECIDE PERMISSÃO. Pasta de empreendimento arquivado não aparece porque a
 * RLS deixa de enxergá-la; o download depende de endereço assinado que o servidor só
 * emite se a mesma política deixar. Botão escondido é cortesia, não tranca.
 */
import { h, dataCurta } from '../dom';
import { icone } from '../icones';
import { cabecalhoDeTela, tentar, vazio } from '../casca';
import type { Vista } from '../casca';
import { avisar } from '../ui/aviso';
import { ir, recarregar } from '../rotas';
import { ehDemonstracao } from '../config';
import { listarEmpreendimentos, listarEmpresas } from '../../../../packages/dados/src/repositorios';
import type { EmpreendimentoResumo } from '../../../../packages/dados/src/repositorios';
import {
  enderecoParaBaixar, listarDocumentosDaEmpresa, removerDocumento, restaurarDocumento,
} from '../../../../packages/dados/src/documentos';
import type { Documento } from '../../../../packages/dados/src/documentos';
import { podeEditar } from '../../../../packages/dados/src/sessao';
import type { Perfil } from '../../../../packages/dados/src/sessao';

/** "1,4 MB" — o usuário não quer contar zeros. */
function tamanho(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1).replace('.', ',')} MB`;
}

const ROTULO_TIPO: Record<string, string> = { mcv: 'MCV', mvt: 'MVT' };

/**
 * Quais pastas ficam abertas. Vive no módulo, e não no `Vista`, porque `recarregar()`
 * remonta a tela inteira depois de remover ou desfazer — sem isto a pasta se fecharia
 * embaixo do dedo da pessoa a cada ação.
 */
const abertas = new Set<string>();

async function baixar(d: Documento): Promise<void> {
  const url = await enderecoParaBaixar(d.caminho);
  /* `noopener`: sem ele a página aberta ganha `window.opener` e pode navegar a nossa por
     baixo. É endereço do nosso próprio servidor, mas a regra não abre exceção por origem. */
  const a = h('a', { href: url, target: '_blank', rel: 'noopener noreferrer', download: d.nome });
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function linhaArquivo(d: Documento, editavel: boolean): HTMLElement {
  const remover = h('button', {
    class: 'iconbtn',
    title: 'Remover',
    html: icone.lixeira(15),
    onclick: (ev: Event) => {
      ev.stopPropagation();
      void tentar(() => removerDocumento(d.id), () => {
        avisar(`"${d.nome}" foi removido.`, {
          tom: 'ok',
          desfazer: () => { void tentar(() => restaurarDocumento(d.id), () => recarregar()); },
        });
        recarregar();
      });
    },
  });

  return h(
    'div',
    {
      class: 'card',
      style: 'padding:11px 14px;display:flex;align-items:center;gap:12px;cursor:pointer;background:transparent',
      title: 'Baixar',
      onclick: () => { void tentar(() => baixar(d)); },
    },
    h('span', { style: 'color:hsl(var(--muted-foreground));flex:none', html: icone.arquivo(16) }),
    h('div', { style: 'display:grid;gap:2px;min-width:0;flex:1' },
      h('span', { style: 'overflow:hidden;text-overflow:ellipsis;white-space:nowrap' }, d.nome),
      h('span', { class: 'muted', style: 'font-size:var(--fs-2xs)' },
        `${tamanho(d.tamanhoBytes)} · ${dataCurta(d.enviadoEm)}`)),
    h('span', { class: 'badge sec', style: 'flex:none' }, ROTULO_TIPO[d.tipo] ?? d.tipo),
    editavel ? remover : null,
  );
}

function pasta(emp: EmpreendimentoResumo, docs: Documento[], editavel: boolean): HTMLElement {
  const aberta = abertas.has(emp.id);
  const miolo = h('div', {
    style: `display:${aberta ? 'grid' : 'none'};gap:6px;padding:4px 10px 12px 40px`,
  });
  if (docs.length === 0) {
    miolo.appendChild(h('div', { class: 'muted', style: 'font-size:var(--fs-2xs);padding:6px 4px' },
      'Pasta vazia. Anexe pelos botões de MCV e MVT dentro de um cenário deste empreendimento.'));
  } else {
    docs.forEach((d) => miolo.appendChild(linhaArquivo(d, editavel)));
  }

  const seta = h('span', {
    class: 'muted',
    style: `flex:none;transition:transform .15s;transform:rotate(${aberta ? 90 : 0}deg)`,
    html: icone.seta(15),
  });
  const marca = h('span', { style: 'color:hsl(var(--primary-text));flex:none', html: aberta ? icone.pastaAberta(19) : icone.pasta(19) });

  const cabeca = h(
    'div',
    {
      style: 'padding:13px 16px;display:flex;align-items:center;gap:12px;cursor:pointer',
      onclick: () => {
        const agora = !abertas.has(emp.id);
        if (agora) abertas.add(emp.id); else abertas.delete(emp.id);
        miolo.style.display = agora ? 'grid' : 'none';
        seta.style.transform = `rotate(${agora ? 90 : 0}deg)`;
        marca.innerHTML = agora ? icone.pastaAberta(19) : icone.pasta(19);
      },
    },
    seta,
    marca,
    h('div', { style: 'display:grid;gap:2px;min-width:0;flex:1' },
      h('span', { style: 'font-weight:640' }, emp.nome),
      h('span', { class: 'muted', style: 'font-size:var(--fs-2xs)' },
        docs.length === 0 ? 'nenhum documento'
          : `${docs.length} documento${docs.length > 1 ? 's' : ''}`)),
  );

  return h('div', { class: 'card acard', style: 'display:grid;padding:0' }, cabeca, miolo);
}

/* ============================================================================
 * A tela
 * ========================================================================== */

export async function telaDocumentos(perfil: Perfil, empresaId: string | null): Promise<Vista> {
  const empresas = await listarEmpresas();

  /* Sem contexto: com UMA incorporadora não há o que perguntar — entra nela. Com várias
     (o caso do Admin Hype), pergunta uma vez; a partir daí a casca lembra e o item da
     barra lateral vem direto para cá. É o único lugar em que a incorporadora reaparece. */
  if (empresaId === null) {
    if (empresas.length === 1) {
      ir({ nome: 'documentos', empresaId: empresas[0]!.id });
      return { trilha: [{ texto: 'Documentos' }], corpo: h('div', {}), ativo: 'documentos' };
    }
    const corpo = h('div', { 'data-el': 'EL-188' });
    corpo.appendChild(cabecalhoDeTela('Documentos',
      'Escolha a incorporadora para ver as pastas dos empreendimentos dela.'));
    corpo.appendChild(
      empresas.length === 0
        ? vazio('Você ainda não tem acesso a nenhuma incorporadora.')
        : h('div', { style: 'display:grid;gap:10px;max-width:900px' },
            ...empresas.map((e) => h('div', {
              class: 'card acard',
              style: 'padding:15px 18px;display:flex;align-items:center;gap:14px;cursor:pointer',
              onclick: () => ir({ nome: 'documentos', empresaId: e.id }),
            },
              h('span', { style: 'color:hsl(var(--primary-text));flex:none', html: icone.empresa(18) }),
              h('span', { style: 'font-weight:640;flex:1' }, e.nome),
              h('span', { class: 'muted', style: 'flex:none', html: icone.seta(16) })))),
    );
    return { trilha: [{ texto: 'Documentos' }], corpo, ativo: 'documentos' };
  }

  const empresa = empresas.find((e) => e.id === empresaId);
  if (empresa === undefined) throw new Error('Esta incorporadora não existe mais, ou saiu do seu acesso.');

  const [empreendimentos, docs] = await Promise.all([
    listarEmpreendimentos(empresaId),
    listarDocumentosDaEmpresa(empresaId),
  ]);
  /* ADM-2: a situação lida AGORA (esta lista) vale sobre o perfil em memória — ver `sessao.ts`. */
  const editavel = podeEditar(perfil, empresaId, empresa.ativa);

  /* Agrupar do lado da tela: o servidor devolveu a incorporadora inteira numa consulta só. */
  const porEmp = new Map<string, Documento[]>();
  for (const d of docs) {
    const lista = porEmp.get(d.empreendimentoId);
    if (lista === undefined) porEmp.set(d.empreendimentoId, [d]); else lista.push(d);
  }

  const corpo = h('div', { 'data-el': 'EL-188' });
  corpo.appendChild(cabecalhoDeTela(
    'Documentos',
    `Arquivos por empreendimento de ${empresa.nome}. Ficam disponíveis para todos da incorporadora.`,
  ));
  /* O portão fechado do checkpoint 3, no lugar em que a pessoa vai agir. */
  if (ehDemonstracao()) {
    corpo.appendChild(h('div', {
      class: 'card',
      style: 'padding:12px 16px;margin-bottom:14px;max-width:900px;display:flex;gap:10px;align-items:flex-start',
    },
      h('span', { style: 'flex:none;color:hsl(var(--warning))', html: icone.alerta(16) }),
      h('span', { class: 'muted', style: 'font-size:var(--fs-2xs)' },
        'Este é o ambiente de demonstração. Use apenas arquivos de teste — '
        + 'documento real de cliente não deve ser anexado aqui.')));
  }
  corpo.appendChild(
    empreendimentos.length === 0
      ? vazio('Nenhum empreendimento nesta incorporadora ainda.')
      : h('div', { style: 'display:grid;gap:10px;max-width:900px' },
          ...empreendimentos.map((e) => pasta(e, porEmp.get(e.id) ?? [], editavel))),
  );

  return {
    trilha: [
      { texto: 'Incorporadoras', rota: { nome: 'incorporadoras' } },
      { texto: empresa.nome, rota: { nome: 'empreendimentos', empresaId } },
      { texto: 'Documentos' },
    ],
    corpo,
    ativo: 'documentos',
    empresaId,
  };
}
